import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { FolderOpenIcon } from 'lucide-react';
import {
  type JSX,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { Button } from '@/components/ui/button.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { ArtifactsView } from '@/features/artifacts/ArtifactsView.js';
import {
  artifactDeepLinkOutcome,
  type ArtifactDeepLinkOutcome,
} from '@/features/artifacts/artifact-location.js';
import {
  normalizeArtifactsSearch,
  oneDrivePositionFromSearch,
  oneDriveSearchFor,
  sharePointConnectionFromSearch,
  sharePointConnectionSearchFor,
  sharePointHitSearchFor,
  sharePointPositionFromSearch,
  sharePointSearchFor,
  type ArtifactsSearch,
  type ArtifactsTab,
  type SharePointTreePosition,
  type OneDrivePosition,
} from '@/features/artifacts/artifacts-search.js';
import { SHAREPOINT_TREE_ROOT } from '@/features/sharepoint/tree-position.js';
import {
  useUiStore,
  type WorkspaceChangeReason,
} from '@/features/settings/ui-store.js';
import {
  resolveCurrentUserRole,
  resolveDefaultWorkspaceId,
  resolveWorkspaceSharing,
} from '@/features/workspace/workspace-access.js';
import { artifactLocationQueryOptions } from '@/lib/api/workspace-artifacts.js';
import {
  workspaceMembersQueryOptions,
  workspaceQueryOptions,
  workspacesQueryOptions,
  type Workspace,
} from '@/lib/api/workspaces.js';
import { notify } from '@/lib/notify.js';
import {
  getState as getIdentityState,
  subscribe as subscribeIdentity,
} from '@/store/identity-store.js';

export const Route = createFileRoute('/artifacts')({
  component: ArtifactsPage,
  /**
   * The section's whole address: which tab is open, which file the preview should show, which SharePoint
   * the tree belongs to and where it is pointed. Blank and unknown values normalize away rather than
   * reaching a component — see `features/artifacts/artifacts-search.ts`, which owns the rules and is
   * tested there.
   */
  validateSearch: normalizeArtifactsSearch,
});

/**
 * `/artifacts` — the workspace's shared file store as its own sidebar section. Scopes to the
 * globally-selected workspace (the sidebar switcher). The current user's id + membership role
 * (resolved from the shell like the manage page) gate sharing and deletion (creator or workspace
 * owner); the workspace-visibility controls are hidden in a personal workspace, while handing a copy
 * out of one is offered there like anywhere else.
 *
 * The page waits for the workspaces list to load, then resolves the workspace to show from the
 * persisted `selectedWorkspaceId` (falling back to the user's default), so a returning member lands
 * on their files rather than flashing the "select a workspace" prompt. The prompt is only reached
 * when the user genuinely has no workspace. `selectedWorkspaceId` is applied synchronously by
 * zustand/persist, so the list-loading gate is all that's needed and the store's `hasHydrated` flag
 * adds nothing here. A failed workspaces fetch (data stays undefined on error) shows an error state
 * with retry.
 *
 * A `?file=` link goes through {@link ArtifactDeepLink} first, because the file may live in a
 * workspace other than the selected one.
 */
function ArtifactsPage(): JSX.Element {
  const selectedWorkspaceId = useUiStore((s) => s.selectedWorkspaceId);
  const search: ArtifactsSearch = useSearch({ from: '/artifacts' });
  const navigate = useNavigate();
  const {
    data: workspaces,
    isError,
    refetch,
  } = useQuery(workspacesQueryOptions());

  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-20 text-center">
        <span className="flex size-12 items-center justify-center rounded-2xl bg-destructive/12 text-destructive">
          <FolderOpenIcon className="size-6" />
        </span>
        <div className="max-w-md space-y-3">
          <p className="font-heading text-base font-semibold">
            Couldn't load your workspaces
          </p>
          <p className="text-sm text-muted-foreground">
            Something went wrong reaching the workspace list. Check your
            connection and try again.
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (workspaces === undefined) return <ArtifactsLoading />;

  const workspaceId = resolveDefaultWorkspaceId(
    workspaces,
    selectedWorkspaceId,
  );
  if (workspaceId === null) {
    return (
      <ArtifactsNotice
        title="Select a workspace"
        body="Pick a workspace from the switcher to see the files shared in it."
      />
    );
  }
  const renderRoute = (viewWorkspaceId: string): JSX.Element => (
    <ArtifactsRoute
      workspaceId={viewWorkspaceId}
      openArtifactId={search.file ?? null}
      tab={search.tab ?? 'files'}
      onTabChange={(tab, options) =>
        void navigate({
          to: '/artifacts',
          search: (previous) => ({
            ...previous,
            tab,
            file: options?.preserveFile ? previous.file : undefined,
          }),
          replace: options?.preserveFile === true,
        })
      }
      sharePointConnectionId={sharePointConnectionFromSearch(search)}
      onSharePointConnectionChange={(connectionId) =>
        void navigate({
          to: '/artifacts',
          /**
           * Changing SharePoint drops the position with it: a `drive_id` or an `item_id` from another
           * tenant addresses nothing, so keeping either would point the tree at ids the new connection
           * has never heard of.
           */
          search: (previous) => ({
            ...previous,
            tab: 'sharepoint' as const,
            ...sharePointConnectionSearchFor(connectionId),
            ...sharePointSearchFor(SHAREPOINT_TREE_ROOT),
          }),
        })
      }
      onSharePointOpenSearchHit={(connectionId, position) =>
        void navigate({
          to: '/artifacts',
          /**
           * A hit moves WHICH SharePoint and where inside it in one step. Reporting the connection on its
           * own would reset the position, and reporting the position on its own would point the tree at
           * another tenant's ids — see `sharePointHitSearchFor`.
           */
          search: (previous) => ({
            ...previous,
            tab: 'sharepoint' as const,
            ...sharePointHitSearchFor(connectionId, position),
          }),
        })
      }
      sharePointPosition={sharePointPositionFromSearch(search)}
      onSharePointNavigate={(position) =>
        void navigate({
          to: '/artifacts',
          /**
           * Every `sp_` key is rewritten on every move, so walking back up actually drops the deeper
           * ids instead of leaving a stale folder behind in the URL.
           */
          search: (previous) => ({
            ...previous,
            tab: 'sharepoint' as const,
            ...sharePointSearchFor(position),
          }),
        })
      }
      oneDrivePosition={oneDrivePositionFromSearch(search)}
      onOneDriveNavigate={(position) =>
        void navigate({
          to: '/artifacts',
          search: (previous) => ({
            ...previous,
            tab: 'onedrive' as const,
            ...oneDriveSearchFor(position),
          }),
        })
      }
    />
  );
  if (search.file === undefined) return renderRoute(workspaceId);
  return (
    <ArtifactDeepLink
      key={search.file}
      file={search.file}
      workspaces={workspaces}
      fallbackWorkspaceId={workspaceId}
      renderRoute={renderRoute}
      onShowMyFiles={() =>
        void navigate({
          to: '/artifacts',
          search: { ...search, file: undefined },
          replace: true,
        })
      }
    />
  );
}

/** The page's placeholder while a read it cannot render without is in flight. */
function ArtifactsLoading(): JSX.Element {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-40 w-full rounded-xl" />
    </div>
  );
}

/** The section's full-pane notice: an icon, a title, one sentence, and optionally a way out. */
function ArtifactsNotice({
  title,
  body,
  children,
}: {
  title: string;
  body: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-20 text-center">
      <span className="flex size-12 items-center justify-center rounded-2xl bg-primary/12 text-primary">
        <FolderOpenIcon className="size-6" />
      </span>
      <div className="max-w-md space-y-1">
        <p className="font-heading text-base font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground">{body}</p>
        {children !== undefined && <div className="pt-2">{children}</div>}
      </div>
    </div>
  );
}

/**
 * `/artifacts?file=` — the linked file may live in a workspace other than the selected one, and the view
 * only looks a file up inside the workspace it is given, so the wrong workspace renders the wrong list
 * with "File unavailable." under it. This asks the locator where the file is first. A found file in one
 * of the caller's workspaces opens there — adopting that workspace as the selection when it differs,
 * the way the chats route adopts a linked chat's workspace. The other answers get a full-pane
 * explanation instead of a list. A locator failure is not an answer: the page then renders what it did
 * before the locator existed, which is what makes a build with this code safe against a gateway
 * without the route.
 *
 * Keyed by the file id from the caller, so a new link starts over — including the one-time adopt.
 */
function ArtifactDeepLink({
  file,
  workspaces,
  fallbackWorkspaceId,
  renderRoute,
  onShowMyFiles,
}: {
  file: string;
  workspaces: readonly Workspace[];
  /** The workspace the page would show without the locator: the resolved selection. */
  fallbackWorkspaceId: string;
  renderRoute: (workspaceId: string) => JSX.Element;
  onShowMyFiles: () => void;
}): JSX.Element {
  const { data: location, isError } = useQuery(
    artifactLocationQueryOptions(file),
  );
  const outcome =
    location === undefined
      ? undefined
      : artifactDeepLinkOutcome(location, workspaces, fallbackWorkspaceId);
  const userSwitched = useFileWorkspaceAdoption(outcome, workspaces);

  if (isError) return renderRoute(fallbackWorkspaceId);
  if (outcome === undefined) return <ArtifactsLoading />;
  if (outcome.kind === 'not-member') {
    return (
      <ArtifactsNotice
        title="You can't open this file here"
        body="This file is in a workspace you are not a member of. Ask whoever sent you the link to share the file with you."
      >
        <ShowMyFilesButton onClick={onShowMyFiles} />
      </ArtifactsNotice>
    );
  }
  if (outcome.kind === 'missing') {
    return (
      <ArtifactsNotice
        title="This file is no longer available"
        body="It was deleted, or you no longer have access to it."
      >
        <ShowMyFilesButton onClick={onShowMyFiles} />
      </ArtifactsNotice>
    );
  }
  return renderRoute(userSwitched ? fallbackWorkspaceId : outcome.workspaceId);
}

/** The way out of a deep link that cannot open: back to the selected workspace's own files. */
function ShowMyFilesButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <Button variant="outline" size="sm" onClick={onClick}>
      Show my files
    </Button>
  );
}

/**
 * Adopts the file's workspace as the selection — once per mount, as a `system` change so nothing that
 * reacts to a user switch tears down — and names the workspace in a toast. Skipped once the user has
 * picked a workspace by hand after the link arrived (their choice wins), and that is also what the
 * returned flag reports, so the caller renders their pick rather than the file's workspace.
 */
function useFileWorkspaceAdoption(
  outcome: ArtifactDeepLinkOutcome | undefined,
  workspaces: readonly Workspace[],
): boolean {
  const { userSwitched, userSwitchedRef } = useUserSwitchedSinceMount();
  const adoptedRef = useRef(false);
  const adoptWorkspaceId =
    outcome?.kind === 'open' && outcome.adopt ? outcome.workspaceId : null;

  useEffect(() => {
    if (adoptWorkspaceId === null || adoptedRef.current) return;
    adoptedRef.current = true;
    if (userSwitchedRef.current) return;
    useUiStore.getState().setSelectedWorkspaceId(adoptWorkspaceId, 'system');
    notify.info(
      `Opened in workspace "${workspaceName(workspaces, adoptWorkspaceId)}"`,
    );
  }, [adoptWorkspaceId, workspaces, userSwitchedRef]);

  return userSwitched;
}

/**
 * Whether the user has changed workspace by hand since mount — a sticky fact, not an inference from
 * the current id: after the link adopted the file's workspace, a switch back to the arrival workspace
 * is still the user's choice, and comparing ids would miss it. It is read off the store's own change
 * stream, so a `system` change (the adopt itself, a rehydration) never counts. The ref answers
 * synchronously for effects; the state re-renders the caller.
 */
function useUserSwitchedSinceMount(): {
  userSwitched: boolean;
  userSwitchedRef: RefObject<boolean>;
} {
  const userSwitchedRef = useRef(false);
  const [userSwitched, setUserSwitched] = useState(false);
  useEffect(
    () =>
      useUiStore.subscribe((state, previous) => {
        if (userSwitchedRef.current || !isUserSwitch(state, previous)) return;
        userSwitchedRef.current = true;
        setUserSwitched(true);
      }),
    [],
  );
  return { userSwitched, userSwitchedRef };
}

/** True when this store change moved the selection and a user did the moving. */
function isUserSwitch(
  state: WorkspaceSelection,
  previous: WorkspaceSelection,
): boolean {
  return (
    state.selectedWorkspaceId !== previous.selectedWorkspaceId &&
    state.lastWorkspaceChangeReason === 'user'
  );
}

/** The two store fields a workspace change is read from. */
type WorkspaceSelection = {
  selectedWorkspaceId: string | null;
  lastWorkspaceChangeReason: WorkspaceChangeReason;
};

/** The workspace's display name; the id when the list does not carry it. */
function workspaceName(
  workspaces: readonly Workspace[],
  workspaceId: string,
): string {
  return (
    workspaces.find((workspace) => workspace.id === workspaceId)?.name ??
    workspaceId
  );
}

/**
 * Resolves the caller's role in the selected workspace, then renders the artifacts view.
 *
 * The role is resolved WITHOUT the platform-admin elevation the rest of the shell uses
 * (`isAdmin: false`). The artifact routes carry no admin bypass by construction — not one of the
 * service's access predicates accepts an admin flag, because an artifact's visibility is its author's
 * choice — so a non-member admin gets a 404 on the list and a member-admin gets exactly what their
 * membership says. Elevating them to `owner` here would only render Delete / Share / Edit controls the
 * service answers 403 to, which is the one thing this section's gating is supposed to prevent.
 *
 * The workspace's own row lags the member directory, so the role can already be resolved — and the section's
 * actions reachable — while the workspace is still `undefined`. That window is a third sharing state rather
 * than a guess, because the two things it decides fail in opposite directions: the knowledge-base
 * confirmation warns about sharing (over-warning costs a sentence, silence costs the file's privacy) while
 * the bulk Share / Make private stay hidden (widening a personal workspace's file means nothing, and the
 * service refuses it). The per-file `Share…` is not one of them — it opens a dialog whose outside half
 * hands a copy out, which any workspace can do. The chat's artifact panel resolves the same state the same
 * way.
 */
function ArtifactsRoute({
  workspaceId,
  openArtifactId,
  tab,
  onTabChange,
  sharePointConnectionId,
  onSharePointConnectionChange,
  onSharePointOpenSearchHit,
  sharePointPosition,
  onSharePointNavigate,
  oneDrivePosition,
  onOneDriveNavigate,
}: {
  workspaceId: string;
  /** A file to open on arrival (`?file=`), or null. */
  openArtifactId: string | null;
  tab: ArtifactsTab;
  onTabChange: (
    tab: ArtifactsTab,
    options?: { preserveFile?: boolean },
  ) => void;
  /** Which SharePoint the tab shows (`?sp_conn=`), or null for the one the service resolves. */
  sharePointConnectionId: string | null;
  onSharePointConnectionChange: (connectionId: string | null) => void;
  /** Opens a folder found by searching, in the SharePoint it was found in. */
  onSharePointOpenSearchHit: (
    connectionId: string | null,
    position: SharePointTreePosition,
  ) => void;
  sharePointPosition: SharePointTreePosition;
  onSharePointNavigate: (position: SharePointTreePosition) => void;
  oneDrivePosition: OneDrivePosition;
  onOneDriveNavigate: (position: OneDrivePosition) => void;
}): JSX.Element {
  const identity = useSyncExternalStore(
    subscribeIdentity,
    getIdentityState,
    getIdentityState,
  );
  const currentUserId = identity.identity?.userId ?? null;

  const { data: workspace } = useQuery(workspaceQueryOptions(workspaceId));
  const { data: members } = useQuery(workspaceMembersQueryOptions(workspaceId));

  const role = resolveCurrentUserRole({
    workspace,
    members,
    currentUserId,
    isAdmin: false,
  });

  return (
    <ArtifactsView
      key={workspaceId}
      workspaceId={workspaceId}
      currentUserId={currentUserId}
      role={role}
      workspaceSharing={resolveWorkspaceSharing(workspace)}
      openArtifactId={openArtifactId}
      tab={tab}
      onTabChange={onTabChange}
      sharePointConnectionId={sharePointConnectionId}
      onSharePointConnectionChange={onSharePointConnectionChange}
      onSharePointOpenSearchHit={onSharePointOpenSearchHit}
      sharePointPosition={sharePointPosition}
      onSharePointNavigate={onSharePointNavigate}
      oneDrivePosition={oneDrivePosition}
      onOneDriveNavigate={onOneDriveNavigate}
    />
  );
}
